import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
} from '@angular/core';

/**
 * Se aplica al contenedor del menú (mat-nav-list). Para cada
 * <a mat-list-item [routerLink]> descendiente:
 *  - inserta on-hover un botón "open_in_new" (compartido entre todos los
 *    ítems) que abre el href del anchor con window.open. Al abrir con
 *    window.open la pestaña nueva conserva window.opener, lo que permite
 *    el traspaso de sesión (ver session-handoff.ts).
 *  - intercepta auxclick (middle-click) y ctrl/cmd+click para abrir
 *    también via window.open en vez del default del navegador, que no
 *    setea opener y por eso la pestaña nueva caía en login.
 */
@Directive({
  selector: '[appAbrirNuevaPestania]',
})
export class AbrirNuevaPestaniaDirective implements OnInit, OnDestroy {
  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private renderer = inject(Renderer2);
  private zone = inject(NgZone);

  private boton?: HTMLButtonElement;
  private unlisteners: (() => void)[] = [];

  ngOnInit(): void {
    const host = this.el.nativeElement;
    this.zone.runOutsideAngular(() => {
      this.unlisteners.push(
        this.renderer.listen(host, 'mouseover', (e: MouseEvent) =>
          this.onMouseover(e),
        ),
        this.renderer.listen(host, 'auxclick', (e: MouseEvent) => {
          if (e.button === 1) this.abrirDesdeEvento(e);
        }),
        this.renderer.listen(host, 'click', (e: MouseEvent) => {
          if (e.button === 0 && (e.ctrlKey || e.metaKey)) {
            this.abrirDesdeEvento(e);
          }
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.unlisteners.forEach((u) => u());
    this.boton?.remove();
  }

  private anchorDe(e: Event): HTMLAnchorElement | null {
    const target = e.target as HTMLElement | null;
    const a = target?.closest?.('a[mat-list-item]');
    return a instanceof HTMLAnchorElement && a.getAttribute('href') ? a : null;
  }

  /** Middle-click / ctrl+click sobre un ítem: window.open propio (con opener). */
  private abrirDesdeEvento(e: MouseEvent): void {
    const anchor = this.anchorDe(e);
    if (!anchor) return; // headers de expansion panel, botones, etc.
    e.preventDefault();
    e.stopPropagation();
    window.open(anchor.href, '_blank');
  }

  /** Muda el botón compartido al ítem bajo el mouse (solo dispositivos con hover). */
  private onMouseover(e: MouseEvent): void {
    if (!window.matchMedia('(hover: hover)').matches) return;
    const anchor = this.anchorDe(e);
    if (!anchor || anchor.contains(this.boton ?? null)) return;
    // Si el anchor que contenía al botón salió del DOM (@if, colapso de
    // panel), el botón murió con él y hay que recrearlo
    if (!this.boton || !this.boton.isConnected) {
      this.boton = this.crearBoton();
    }
    this.renderer.appendChild(anchor, this.boton);
  }

  private crearBoton(): HTMLButtonElement {
    const b: HTMLButtonElement = this.renderer.createElement('button');
    b.type = 'button';
    b.tabIndex = -1;
    // Botón de ícono del sistema (componentes/botones), armado a mano porque
    // se inserta por código dentro del ítem del menú
    b.className = 'irx-boton-icono irx-boton-icono--mini boton-nueva-pestania';
    b.title = 'Abrir en pestaña nueva';
    b.setAttribute('aria-label', 'Abrir en pestaña nueva');
    const icono: HTMLSpanElement = this.renderer.createElement('span');
    icono.className = 'material-icons';
    icono.textContent = 'open_in_new';
    this.renderer.appendChild(b, icono);
    this.unlisteners.push(
      this.renderer.listen(b, 'click', (e: MouseEvent) => {
        // No disparar RouterLink ni (click)="toggleSidenav()" del <a> padre
        e.preventDefault();
        e.stopPropagation();
        const anchor = b.closest(
          'a[mat-list-item]',
        ) as HTMLAnchorElement | null;
        if (anchor?.href) window.open(anchor.href, '_blank');
      }),
    );
    return b;
  }
}
